import { Box, Typography, useTheme } from '@mui/material';
import React from 'react';

import { PriorityDot } from '../../atoms/PriorityDot';
import { StatusBadge } from '../../atoms/StatusBadge';

interface PreviewDrawerSummaryProps {
  service: Record<string, unknown> | null;
}

interface FieldRowProps {
  label: string;
  children: React.ReactNode;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, children }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.75,
        borderBottom: `1px solid ${theme.palette.v4.border}`,
      }}
    >
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right', maxWidth: '60%' }}>{children}</Box>
    </Box>
  );
};

const formatDate = (value: unknown): string => {
  if (!value || typeof value !== 'string') return '\u2014';
  try {
    return new Date(value).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '\u2014';
  }
};

const str = (value: unknown): string =>
  typeof value === 'string' && value.length > 0 ? value : '\u2014';

/** Extract a nested field, handling both detail API and list API shapes. */
const getField = (svc: Record<string, unknown>, ...keys: string[]): string => {
  for (const k of keys) {
    const v = svc[k];
    if (typeof v === 'string' && v.length > 0) return v;
    if (typeof v === 'object' && v !== null && 'name' in (v as Record<string, unknown>)) {
      const name = (v as Record<string, unknown>).name;
      if (typeof name === 'string') return name;
    }
  }
  return '\u2014';
};

export const PreviewDrawerSummary: React.FC<PreviewDrawerSummaryProps> = ({ service }) => {
  if (!service) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No service data available</Typography>;
  }

  const statusName = getField(service, 'serviceStatusName', 'serviceStatus');
  const priorityLabel = getField(service, 'priorityLabel', 'priorityCode');
  const client = getField(service, 'client', 'clientHierarchy', 'clientName');
  const location = getField(service, 'mainLocation', 'locationName');
  const technician = (() => {
    const alloc = service.primaryAllocation as Record<string, unknown> | undefined;
    const node = alloc?.node as Record<string, unknown> | undefined;
    if (typeof node?.name === 'string') return node.name;
    if (typeof service.primaryAllocation === 'string') return service.primaryAllocation;
    return '\u2014';
  })();
  const jobType = getField(service, 'serviceTypeName') !== '\u2014'
    ? getField(service, 'serviceTypeName')
    : (() => {
      const types = service.serviceType as Array<{ name: string }> | undefined;
      return types?.[0]?.name ?? str(service.serviceTypeCode);
    })();

  return (
    <Box>
      <Typography variant="overline" sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: 1.2, color: 'text.secondary', mb: 1, display: 'block' }}>
        SUMMARY
      </Typography>
      <FieldRow label="Status">
        <StatusBadge status={statusName} size="small" />
      </FieldRow>
      <FieldRow label="Priority">
        <PriorityDot priority={priorityLabel} />
      </FieldRow>
      <FieldRow label="Job Type">
        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{jobType}</Typography>
      </FieldRow>
      <FieldRow label="Client">
        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{client}</Typography>
      </FieldRow>
      <FieldRow label="Location">
        <Typography variant="body2" sx={{ fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {location}
        </Typography>
      </FieldRow>
      <FieldRow label="Technician">
        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{technician}</Typography>
      </FieldRow>
      <FieldRow label="Created">
        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{formatDate(service.createdAt)}</Typography>
      </FieldRow>
    </Box>
  );
};
